import { ParamMap, Params } from '@angular/router';
import moment from 'moment';
import { IScheduleCollapsedRows, IScheduleViewOptions, IVehicleScheduleFilters } from 'src/app/models';
import {
  ExpeditionType,
  ScheduleGroupBy,
  ScheduleViewMode,
  ScheduleViewOption,
  ServiceType,
} from 'src/app/models/enums';
import { normalizeViewOptions } from 'src/app/components/vehicles/scheduleViewOptions';
import {
  hasAnyFilterKey,
  parseDate,
  parseEnumList,
  parseIdList,
  parseNumberList,
  parseStoredDate,
  serializeIdList,
  serializeNumberList,
  serializeStringList,
} from './filterUtils';

export const VEHICLE_FILTER_KEYS = [
  'clients', 'operators', 'types', 'serviceTypes', 'towns', 'services', 'routes', 'vehicles', 'drivers', 'from', 'to'
] as const;

export function serializeVehiclesFilters(f: IVehicleScheduleFilters): Params {
  return {
    clients: serializeIdList(f.clients),
    operators: serializeIdList(f.operators),
    types: serializeStringList(f.types),
    serviceTypes: serializeStringList(f.serviceTypes),
    towns: serializeIdList(f.zones),
    services: serializeIdList(f.services),
    routes: serializeIdList(f.routes),
    vehicles: serializeIdList(f.vehicles),
    drivers: serializeIdList(f.drivers),
    from: f.from ? moment(f.from).format('YYYY-MM-DD') : null,
    to: f.to ? moment(f.to).format('YYYY-MM-DD') : null,
  };
}

export function deserializeVehiclesFilters(params: ParamMap): IVehicleScheduleFilters | undefined {
  if (!hasAnyFilterKey(params, VEHICLE_FILTER_KEYS)) return undefined;
  return {
    clients: parseIdList(params.get('clients')),
    operators: parseIdList(params.get('operators')),
    types: parseEnumList<ExpeditionType>(params.get('types')),
    serviceTypes: parseEnumList<ServiceType>(params.get('serviceTypes')),
    zones: parseIdList(params.get('towns')),
    services: parseIdList(params.get('services')),
    routes: parseIdList(params.get('routes')),
    vehicles: parseIdList(params.get('vehicles')),
    drivers: parseIdList(params.get('drivers')),
    from: parseDate(params.get('from')),
    to: parseDate(params.get('to')),
  };
}

export function serializeVehiclesViewMode(viewMode: ScheduleViewMode): Params {
  return { view: viewMode };
}

export function deserializeVehiclesViewMode(params: ParamMap): ScheduleViewMode {
  const view = params.get('view') as ScheduleViewMode;
  return Object.values(ScheduleViewMode).includes(view) ? view : ScheduleViewMode.DAY;
}

export function serializeVehiclesGroupBy(groupBy: ScheduleGroupBy): Params {
  return { groupBy: groupBy === ScheduleGroupBy.VEHICLES ? null : groupBy };
}

export function deserializeVehiclesGroupBy(params: ParamMap): ScheduleGroupBy {
  const groupBy = params.get('groupBy') as ScheduleGroupBy;
  return Object.values(ScheduleGroupBy).includes(groupBy) ? groupBy : ScheduleGroupBy.VEHICLES;
}

export function serializeVehiclesViewOptions(viewOptions: IScheduleViewOptions): Params {
  return Object.fromEntries(Object.values(ScheduleViewOption).map((option) => [option, String(viewOptions[option])]));
}

export function deserializeVehiclesViewOptions(params: ParamMap): IScheduleViewOptions | undefined {
  const optionsInUrl = Object.values(ScheduleViewOption).filter((option) => params.has(option));
  if (!optionsInUrl.length) return undefined;
  return normalizeViewOptions(Object.fromEntries(optionsInUrl.map((option) => [option, params.get(option) === 'true'])));
}

export function serializeVehiclesCollapsedRows(collapsedRows: IScheduleCollapsedRows): Params {
  return {
    collapsedRows: serializeNumberList(collapsedRows.collapsed),
    expandedRows: serializeNumberList(collapsedRows.expanded),
  };
}

export function deserializeVehiclesCollapsedRows(params: ParamMap): IScheduleCollapsedRows {
  return {
    collapsed: parseNumberList(params.get('collapsedRows')),
    expanded: parseNumberList(params.get('expandedRows')),
  };
}

export function normalizeVehiclesCollapsedRows(stored: unknown): IScheduleCollapsedRows {
  const values = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {};
  const numbers = (value: unknown) => (Array.isArray(value) ? value.filter(Number.isInteger) : []);
  return { collapsed: numbers(values['collapsed']), expanded: numbers(values['expanded']) };
}

export function deserializeVehiclesSelection(params: ParamMap): number | undefined {
  const id = Number(params.get('id'));
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

export function sameVehiclesFilters(a?: IVehicleScheduleFilters, b?: IVehicleScheduleFilters): boolean {
  const key = (f?: IVehicleScheduleFilters) => (f ? JSON.stringify(serializeVehiclesFilters(f)) : '');
  return key(a) === key(b);
}

export function parseVehiclesFiltersFromStorage(json: string | null | undefined): IVehicleScheduleFilters | null {
  if (!json) return null;
  try {
    const f = JSON.parse(json);
    const nn = <T>(arr: T[] | null | undefined): T[] => (arr ?? []).filter((x) => x != null);
    return {
      clients: nn(f.clients),
      operators: nn(f.operators),
      types: nn(f.types),
      serviceTypes: nn(f.serviceTypes),
      zones: nn(f.zones),
      services: nn(f.services),
      routes: nn(f.routes),
      vehicles: nn(f.vehicles),
      drivers: nn(f.drivers),
      from: parseStoredDate(f.from),
      to: parseStoredDate(f.to),
    };
  } catch {
    return null;
  }
}
